<template>
    <!-- Banner: Desktop layout -->
    <div
        v-if="showBanner"
        class="freemium-banner-desktop hidden md:flex w-full p-4 items-center gap-8 rounded-lg border border-[#FEAF9F] dark:border-orange-500/50 bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/50 mb-6"
    >
        <img
            src="/images/freemium-banner-promostar.png"
            alt="Upgrade"
            class="w-9 h-9 shrink-0"
        />
        <div class="flex flex-col items-start gap-1 flex-1">
            <h3
                class="text-slate-800 dark:text-slate-100 text-base font-medium leading-6 m-0"
            >
                Unlock unlimited benefits tracking
            </h3>
            <p
                class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 m-0 self-stretch"
            >
                Free plan supports 1 card. Upgrade to 10xTravel+ for unlimited
                tracking.
            </p>
        </div>
        <div class="flex items-center gap-2 shrink-0">
            <button
                type="button"
                class="flex py-2 px-4 items-start gap-2 rounded bg-transparent text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 cursor-pointer transition-colors hover:bg-gray-100 dark:hover:bg-slate-700"
                @click="dismissBanner"
            >
                Dismiss
            </button>
            <button
                type="button"
                class="flex py-2 px-4 justify-center items-center gap-2 rounded bg-orange text-white text-sm font-medium leading-5 cursor-pointer shadow-sm transition-colors hover:bg-orange-600"
                @click="handleUpgrade"
            >
                Upgrade
            </button>
        </div>
    </div>

    <!-- Banner: Mobile layout -->
    <div
        v-if="showBanner"
        class="freemium-banner-mobile flex md:hidden w-full p-4 flex-col items-center gap-8 rounded-lg border border-[#FEAF9F] dark:border-orange-500/50 shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_0_rgba(0,0,0,0.06)] dark:shadow-slate-900/50 mb-6"
    >
        <!-- Top row: icon + content container -->
        <div class="flex w-full items-start gap-3">
            <img
                src="/images/freemium-banner-promostar.png"
                alt="Upgrade"
                class="w-9 h-9 shrink-0"
            />
            <!-- Content container -->
            <div class="flex flex-col justify-center items-start flex-1">
                <!-- Message container -->
                <div class="flex flex-col items-start gap-1">
                    <h3
                        class="text-slate-800 dark:text-slate-100 text-base font-medium leading-6 m-0"
                    >
                        Unlock unlimited tracking
                    </h3>
                    <p
                        class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 m-0"
                    >
                        Free plan supports 1 card. Upgrade to 10xTravel+ for
                        unlimited tracking.
                    </p>
                </div>
                <!-- Footer: buttons -->
                <div class="flex items-center gap-2 pt-4">
                    <button
                        type="button"
                        class="flex py-2 px-4 justify-center items-center gap-2 rounded bg-orange text-white text-sm font-medium leading-5 cursor-pointer shadow-sm transition-colors hover:bg-orange-600"
                        @click="handleUpgrade"
                    >
                        Upgrade
                    </button>
                    <button
                        type="button"
                        class="flex py-2 px-4 items-center gap-2 rounded bg-transparent text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 cursor-pointer transition-colors hover:bg-gray-100 dark:hover:bg-slate-700"
                        @click="dismissBanner"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Upgrade Modal -->
    <UpgradeModal :show="showUpgradeModal" @close="showUpgradeModal = false" />
</template>

<script setup>
import { ref, computed } from 'vue';
import { useCardBenefitsStore } from '@/store/card-benefits';
import { storeToRefs } from 'pinia';
import UpgradeModal from '@/modals/upgrade-modal.vue';

const cardBenefitsStore = useCardBenefitsStore();
const { isSubscribed, bannerDismissed, freemiumLoaded } =
    storeToRefs(cardBenefitsStore);

const showUpgradeModal = ref(false);

// SUPPORT-722: the banner must only render for a *confirmed* free user.
// Reading `!isSubscribed` alone lets the banner flash during the loading
// interval — the store defaults `isSubscribed` to false, so before
// `/wallets/{id}/card-benefits` resolves every user (including confirmed
// 10xTravel+ members) briefly matches `!isSubscribed`. Gate rendering on
// `freemiumLoaded`, which flips to true only after `setBenefitsData()` has
// applied the API response.
const showBanner = computed(() => {
    return (
        freemiumLoaded.value && !isSubscribed.value && !bannerDismissed.value
    );
});

const dismissBanner = async () => {
    await cardBenefitsStore.dismissBanner();
};

const handleUpgrade = () => {
    showUpgradeModal.value = true;
};
</script>

<style scoped>
.freemium-banner-desktop {
    background:
        linear-gradient(
            66deg,
            rgba(253, 96, 29, 0) 25.49%,
            rgba(253, 96, 29, 0.12) 77.35%,
            rgba(253, 96, 29, 0) 112.21%
        ),
        #fff;
}
:where(.dark) .freemium-banner-desktop {
    background:
        linear-gradient(
            66deg,
            rgba(253, 96, 29, 0) 25.49%,
            rgba(253, 96, 29, 0.18) 77.35%,
            rgba(253, 96, 29, 0) 112.21%
        ),
        rgb(30, 41, 59);
}
.freemium-banner-mobile {
    background:
        radial-gradient(
            95.84% 84.35% at 22.45% 20.06%,
            rgba(253, 96, 29, 0) 29.86%,
            rgba(253, 96, 29, 0.12) 90.75%
        ),
        #fff;
}
:where(.dark) .freemium-banner-mobile {
    background:
        radial-gradient(
            95.84% 84.35% at 22.45% 20.06%,
            rgba(253, 96, 29, 0) 29.86%,
            rgba(253, 96, 29, 0.18) 90.75%
        ),
        rgb(30, 41, 59);
}
</style>
